import React, { useState, useEffect } from "react";
import styles from "./asset/PromptEditor.module.scss";
import { chatbotService } from "../../../api/chatbotService";

/**
 * PromptEditor Component
 * Allows viewing and editing system prompts for the chatbot
 *
 * @param {Object} props
 * @param {string} props.sessionId - Current chat session ID
 * @param {boolean} props.answerConfirmed - Whether answer is confirmed
 */
const PromptEditor = ({ sessionId, answerConfirmed = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState("");
    const [isCustom, setIsCustom] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Load current prompt when editor opens
    useEffect(() => {
        if (isOpen) {
            loadCurrentPrompt();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, answerConfirmed]);

    const loadCurrentPrompt = async () => {
        setIsLoading(true);
        setMessage({ type: "", text: "" });
        try {
            const response = await chatbotService.getCurrentPrompt(sessionId, answerConfirmed);
            setEditedPrompt(response.prompt);
            setIsCustom(response.is_custom);
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to load prompt: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!sessionId) {
            setMessage({
                type: "error",
                text: "No active session. Please start a chat first."
            });
            return;
        }

        if (!editedPrompt || !editedPrompt.trim()) {
            setMessage({
                type: "error",
                text: "Prompt cannot be empty."
            });
            return;
        }

        setIsSaving(true);
        setMessage({ type: "", text: "" });
        try {
            // Ensure answerConfirmed is boolean
            const isConfirmed = Boolean(answerConfirmed);
            console.log("💾 Saving prompt:", {
                sessionId,
                promptLength: editedPrompt.length,
                answerConfirmed: isConfirmed
            });

            await chatbotService.setSessionPrompt(sessionId, editedPrompt, isConfirmed);
            setIsCustom(true);
            setMessage({
                type: "success",
                text: "Custom prompt saved successfully!"
            });
        } catch (error) {
            console.error("Failed to save prompt:", error);
            setMessage({
                type: "error",
                text: `Failed to save prompt: ${error.message}`
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        if (!sessionId) {
            setMessage({
                type: "error",
                text: "No active session."
            });
            return;
        }

        setIsLoading(true);
        setMessage({ type: "", text: "" });
        try {
            // Clear custom prompt for session
            await chatbotService.clearSessionPrompt(sessionId);

            // Load default prompt
            const response = await chatbotService.getDefaultPrompt(answerConfirmed);
            setEditedPrompt(response.prompt);
            setIsCustom(false);
            setMessage({
                type: "success",
                text: "Prompt reset to default successfully!"
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to reset prompt: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDefault = async () => {
        setIsLoading(true);
        setMessage({ type: "", text: "" });
        try {
            const response = await chatbotService.getDefaultPrompt(answerConfirmed);
            setEditedPrompt(response.prompt);
            setMessage({
                type: "info",
                text: "Viewing default prompt. Save to apply it to this session."
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to load default prompt: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.promptEditorContainer}>
            {/* Toggle Button */}
            <button
                className={styles.toggleButton}
                onClick={() => setIsOpen(!isOpen)}
                title="Prompt Editor"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 15.5L9.5 17L12 14.5L14.5 17L16 15.5L13.5 13L16 10.5L14.5 9L12 11.5L9.5 9L8 10.5L10.5 13L8 15.5Z"
                        fill="currentColor"
                    />
                </svg>
                {isCustom && <span className={styles.customIndicator}></span>}
            </button>

            {/* Editor Modal */}
            {isOpen && (
                <div className={styles.editorModal}>
                    <div className={styles.modalHeader}>
                        <h3>System Prompt Editor</h3>
                        <button
                            className={styles.closeButton}
                            onClick={() => setIsOpen(false)}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        {/* Status Badge */}
                        <div className={styles.statusBadge}>
                            <span className={isCustom ? styles.custom : styles.default}>
                                {isCustom ? "Custom Prompt" : "Default Prompt"}
                            </span>
                            <span className={answerConfirmed ? styles.confirmed : styles.notConfirmed}>
                                {answerConfirmed ? "Answer Confirmed" : "Not Confirmed"}
                            </span>
                        </div>

                        {/* Message Display */}
                        {message.text && (
                            <div className={`${styles.message} ${styles[message.type]}`}>
                                {message.text}
                            </div>
                        )}

                        {/* Prompt Editor */}
                        {isLoading ? (
                            <div className={styles.loading}>Loading prompt...</div>
                        ) : (
                            <textarea
                                className={styles.promptTextarea}
                                value={editedPrompt}
                                onChange={(e) => setEditedPrompt(e.target.value)}
                                placeholder="Enter system prompt..."
                                rows={20}
                            />
                        )}

                        {/* Action Buttons */}
                        <div className={styles.actionButtons}>
                            <button
                                className={styles.btnPrimary}
                                onClick={handleSave}
                                disabled={isSaving || isLoading || !editedPrompt.trim()}
                            >
                                {isSaving ? "Saving..." : "Save Custom Prompt"}
                            </button>

                            <button
                                className={styles.btnSecondary}
                                onClick={handleViewDefault}
                                disabled={isLoading}
                            >
                                View Default
                            </button>

                            <button
                                className={styles.btnWarning}
                                onClick={handleReset}
                                disabled={isLoading || !isCustom}
                            >
                                Reset to Default
                            </button>
                        </div>

                        {/* Helper Text */}
                        <div className={styles.helperText}>
                            <p><strong>Note:</strong> Custom prompts are session-specific and won't affect other chat sessions.</p>
                            <p>The prompt changes based on whether the answer is confirmed or not.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromptEditor;
